/**
 * ScheduleChapterDialog.tsx — 书籍级发布计划（books 域）。
 *
 * 发布计划属于 novel，不再把定时配置写在单章上。启用后，首次发布请求写入书级队列并由
 * C 端调度器在计划时刻推进；关闭后，首次发布恢复同步路径。时间输入是书籍语言时区下的墙上时刻。
 */

import type { PublishScheduleResponse } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { chaptersApi } from '~/hooks/use-books';
import { fmtDateTimeInTimeZone } from '~/lib/format';
import { Dialog } from '~/ui/Dialog';
import { toast } from '~/ui/Toast';

export function ScheduleChapterDialog({
  novelId,
  schedule,
  timeZone,
  onClose,
}: {
  novelId: string;
  schedule: PublishScheduleResponse | undefined;
  timeZone: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  // API 读值是绝对 UTC，不能无损还原原始墙上时刻；编辑时要求运营重新确认目标时刻。
  const [localValue, setLocalValue] = useState('');
  const valid = timeZone != null && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(localValue);

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['publish-schedule', novelId] }),
      queryClient.invalidateQueries({ queryKey: ['publish-queue', novelId] }),
      queryClient.invalidateQueries({ queryKey: ['chapters', novelId] }),
      queryClient.invalidateQueries({ queryKey: ['books'] }),
      queryClient.invalidateQueries({ queryKey: ['book-logs', novelId] }),
    ]);

  const setSchedule = useMutation({
    mutationFn: (enabled: boolean) =>
      chaptersApi.setPublishSchedule(
        novelId,
        enabled ? { enabled: true, scheduled_publish_at: `${localValue}:00` } : { enabled: false },
      ),
    onSuccess: async (result) => {
      toast(result.enabled ? '定时发布计划已启用' : '定时发布计划已关闭', {
        tone: 'pos',
        icon: 'IconCheck',
      });
      await invalidate();
      onClose();
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '发布计划保存失败', {
        tone: 'neg',
        icon: 'IconAlert',
      }),
  });

  return (
    <Dialog
      title="发布计划"
      subtitle="按当前语言版本统一控制首次发布路径"
      icon="IconClock"
      width={460}
      onClose={onClose}
      footer={
        <div className="hstack" style={{ gap: 8, justifyContent: 'flex-end' }}>
          {schedule?.enabled && (
            <button
              className="btn"
              disabled={setSchedule.isPending}
              onClick={() => setSchedule.mutate(false)}
            >
              关闭定时发布
            </button>
          )}
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button
            className="btn btn-accent"
            disabled={!valid || setSchedule.isPending}
            onClick={() => setSchedule.mutate(true)}
          >
            {setSchedule.isPending ? '保存中…' : '启用定时发布'}
          </button>
        </div>
      }
    >
      <div className="vstack" style={{ gap: 14, padding: 16 }}>
        <div
          className="card-flat"
          style={{
            padding: '9px 11px',
            background: schedule?.enabled ? 'var(--warn-soft)' : 'var(--bg-subtle)',
          }}
        >
          <div style={{ fontSize: 12.5, fontWeight: 600 }}>
            当前：{schedule?.enabled ? '定时发布' : '立即发布'}
          </div>
          {schedule?.enabled && schedule.scheduled_publish_at && (
            <div className="mono" style={{ marginTop: 3, fontSize: 11, color: 'var(--fg-muted)' }}>
              下次计划 · {fmtDateTimeInTimeZone(schedule.scheduled_publish_at, timeZone)}
            </div>
          )}
        </div>
        <div className="vstack" style={{ gap: 6 }}>
          <span className="label">
            发布时刻（{timeZone ? `书籍语言时区 ${timeZone}` : '书籍语言时区未配置'}）
          </span>
          <input
            type="datetime-local"
            className="input"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
          />
          <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
            {timeZone
              ? '启用后，章节首次发布会进入书级队列；待重发布草稿仍由运营显式重新发布。'
              : '当前语言缺少有效 IANA 时区，无法启用定时发布；请先修复系统语言配置。'}
          </span>
        </div>
      </div>
    </Dialog>
  );
}
